import { eventChannel, EventChannel } from 'redux-saga';
import { ForkEffect } from 'redux-saga/effects';
import { call, getContext, put, race, take, takeLatest } from 'typed-redux-saga';
import { followWithRetry } from '../utils/followWithRetry';
import type { SubscriptionFollowOutcome } from '../types/SubscriptionFollow.types';
import { LiveMapActions } from './liveMapSlice';
import type { OpenLiveMapRequestAction } from './liveMapActions.types';
import { getB50MapData, getCv50MapData } from './utils/mapData';
import { getDeviceMapLocationHistory } from './utils/deviceMapLocation';
import { millimetresToMetres } from './utils/mapCoordinates';
import type { DeviceMapLocationPoint, LiveMapBackgroundData, LivePosition } from './types/LiveMap.types';
import { getPraDeviceId } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/utils/praDeviceId';
import type { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';
import type { DeviceMapPositionUpdate } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

type DevicePositionChannelEvent =
  | { kind: 'data'; payload: DeviceMapPositionUpdate }
  | { kind: 'error'; error: unknown };

const toLivePosition = (point: DeviceMapLocationPoint): LivePosition => ({
  x: point.coordinateX,
  y: point.coordinateY,
  heading: point.coordinateT ?? null,
  lastUpdated: point.updatedAt,
});

// SagaUtils.createChannelFromObservable only logs errors, it doesn't forward them into the
// channel — this distinguishes a data event from an error event so disconnects stay visible.
const createDevicePositionChannel = (
  subscribeToDeviceMapPosition: IDependencies['robotCleaningControlService']['subscribeToDeviceMapPosition'],
  deviceId: string,
  materialNumber: string | undefined,
  serialNumber: string | undefined
): EventChannel<DevicePositionChannelEvent> =>
  eventChannel<DevicePositionChannelEvent>(emit => {
    const subscription = subscribeToDeviceMapPosition({ deviceId, materialNumber, serialNumber }).subscribe({
      next: result => {
        if (result.data?.deviceMapPosition) {
          emit({ kind: 'data', payload: result.data.deviceMapPosition });
        }
      },
      error: error => emit({ kind: 'error', error }),
    });

    return () => subscription.unsubscribe();
  });

// Fetches the Route/map data and location history and shapes them into the success payload. A
// real generator (not a plain async function) so getB50MapData/getDeviceMapLocationHistory stay
// individually-stubbable call() effects for redux-saga-test-plan, same as in openLiveMapSaga.
// No explicit Generator<...> return-type annotation: call() needs its yield type to structurally
// match StrictEffect to correctly infer OpenLiveMapSuccessPayload as the return type
// (SagaReturnType falls back to the whole Generator otherwise) — let TS infer it instead.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchLiveMapData(
  robotCleaningControlService: IDependencies['robotCleaningControlService'],
  model: OpenLiveMapRequestAction['payload']['model'],
  routeId: string | undefined,
  materialNumber: string,
  serialNumber: string
) {
  // Not a ternary: TS loses the union's discriminating narrowing ('plannedPath' in background
  // below) when each branch of a ternary is itself a yield* call().
  let background: LiveMapBackgroundData | Omit<LiveMapBackgroundData, 'plannedPath'>;
  if (model === 'B50') {
    background = yield* call(getB50MapData, robotCleaningControlService, routeId, materialNumber, serialNumber);
  } else {
    background = yield* call(getCv50MapData, robotCleaningControlService, materialNumber, serialNumber);
  }

  const fetchedHistory = yield* call(
    getDeviceMapLocationHistory,
    robotCleaningControlService,
    materialNumber,
    serialNumber
  );
  // The CV50 API reports millimetres.
  const history =
    model === 'CV50'
      ? fetchedHistory.map(point => ({
          ...point,
          coordinateX: millimetresToMetres(point.coordinateX),
          coordinateY: millimetresToMetres(point.coordinateY),
        }))
      : fetchedHistory;
  const lastHistoryPoint = history[history.length - 1];

  return {
    mapInfo: background.mapInfo,
    mapImageUrl: background.mapImageUrl,
    plannedPath: 'plannedPath' in background ? background.plannedPath : [],
    trail: history.map(point => ({
      x: point.coordinateX,
      y: point.coordinateY,
      updatedAt: point.updatedAt,
    })),
    livePosition: lastHistoryPoint ? toLivePosition(lastHistoryPoint) : null,
    cleaningZones: background.cleaningZones ?? [],
    cleaningZoneFeatures: background.cleaningZoneFeatures ?? null,
    activeCleaningZoneIds: background.activeCleaningZoneIds ?? null,
  };
}

// Follows the position until close; a subscription error marks the map disconnected.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* followDevicePosition(
  robotCleaningControlService: IDependencies['robotCleaningControlService'],
  model: OpenLiveMapRequestAction['payload']['model'],
  praDeviceId: string,
  materialNumber: string | undefined,
  serialNumber: string | undefined
): Generator<unknown, SubscriptionFollowOutcome, any> {
  const channel = yield* call(
    createDevicePositionChannel,
    robotCleaningControlService.subscribeToDeviceMapPosition,
    praDeviceId,
    materialNumber,
    serialNumber
  );

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { event } = yield* race({ closed: take(LiveMapActions.closeLiveMap), event: take(channel) });

      if (!event) {
        return { kind: 'stopped' };
      }

      if (event.kind === 'error') {
        console.error(event.error);
        yield* put(LiveMapActions.subscriptionStatusChanged({ status: 'disconnected' }));

        return { kind: 'failed', error: event.error };
      }

      const { coordinateX, coordinateY, coordinateT, lastUpdated } = event.payload;

      // A partial update isn't a real position; drop it rather than default to origin/now.
      if (coordinateX == null || coordinateY == null || lastUpdated == null) {
        console.error('liveMapSaga: dropped deviceMapPosition update missing required fields', event.payload);
      } else {
        yield* put(
          LiveMapActions.deviceMapPositionReceived({
            x: model === 'CV50' ? millimetresToMetres(coordinateX) : coordinateX,
            y: model === 'CV50' ? millimetresToMetres(coordinateY) : coordinateY,
            heading: coordinateT ?? null,
            lastUpdated,
          })
        );
      }
    }
  } finally {
    channel.close();
  }
}

// No manual effect-union type here — typed-redux-saga infers it from call/put/race/take, and a
// precise TNext breaks expectSaga's SagaType in tests.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* openLiveMapSaga(action: OpenLiveMapRequestAction): Generator<unknown, void, any> {
  const { robotCleaningControlService } = yield* getContext<IDependencies>('dependencies');
  const { model, routeId, materialNumber, serialNumber } = action.payload;

  try {
    // Races the initial fetch against an early close (e.g. the modal closes while the Route/map
    // and location-history requests are still in flight) — without this, a close here is missed
    // entirely and the saga still opens a device-position subscription nothing will ever stop.
    // call() (not fork()): an error thrown inside the fetch must propagate into this try/catch the
    // normal way, which an attached fork's error does not.
    const { fetched, closed } = yield* race({
      fetched: call(fetchLiveMapData, robotCleaningControlService, model, routeId, materialNumber, serialNumber),
      closed: take(LiveMapActions.closeLiveMap),
    });

    if (closed || !fetched) {
      return;
    }

    yield* put(LiveMapActions.openLiveMapSuccess(fetched));

    // The subscription needs the PRA device id (not the machine id). Without it, don't subscribe.
    // Raced against a close too: a close during this lookup would otherwise be missed, leaving a
    // position subscription nothing will ever stop.
    const { praDeviceId, closed: closedDuringLookup } = yield* race({
      praDeviceId: call(getPraDeviceId, robotCleaningControlService, materialNumber, serialNumber),
      closed: take(LiveMapActions.closeLiveMap),
    });

    if (closedDuringLookup) {
      return;
    }

    if (!praDeviceId) {
      yield* put(LiveMapActions.subscriptionStatusChanged({ status: 'disconnected' }));

      return;
    }

    // While a failed subscription waits to be opened again, the live map stays disconnected.
    yield* followWithRetry({
      follow: () => followDevicePosition(robotCleaningControlService, model, praDeviceId, materialNumber, serialNumber),
      stop: LiveMapActions.closeLiveMap,
    });
  } catch (error) {
    console.error(error);
    yield* put(LiveMapActions.openLiveMapError({ error }));
  }
}

export function* liveMapSaga(): Generator<ForkEffect<never>, void> {
  yield* takeLatest(LiveMapActions.openLiveMapRequest, openLiveMapSaga);
}
